import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { guardarPDF } from "../pages/compras/helpers";
import { fechaReporte } from "./reportes";

// Fix EZ (2026-10-05): "El cierre de la caja chica debe tener un informe en pdf con el
// que se cierra ese capítulo de caja chica" (CEN, OBS SISTEMA).
const AZUL = [46, 109, 164];
const gs = (v) => `Gs. ${Math.round(Number(v) || 0).toLocaleString("es-PY")}`;
const sinFactura = (g) => g.comprobante_tipo === "sin_factura" || (!g.comprobante_tipo && !/\d/.test(g.invoice_number || g.numero_comprobante || ""));
const totalDe = (g) => Number(g.total_with_iva) || Math.round((Number(g.amount) || 0) * 1.1);

export function pdfCierreCajaChica({ cierre, gastos }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...AZUL);
  doc.text(`Cierre de Caja Chica ${cierre.numero || ""}`, 10, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(70, 70, 70);
  const estado = cierre.estado === "repuesto"
    ? (Number(cierre.monto_reposicion) > 0 ? `Repuesto${cierre.repuesto_at ? ` el ${fechaReporte(cierre.repuesto_at)}` : ""}${cierre.repuesto_por ? ` por ${cierre.repuesto_por}` : ""}` : "Sin diferencia a reponer")
    : "Reposición solicitada";
  doc.text(`CEN Ingeniería · Período ${fechaReporte(cierre.desde)} al ${fechaReporte(cierre.hasta)} · Cerrado el ${fechaReporte(cierre.created_at)} por ${cierre.solicitado_por || cierre.created_by_name || "—"} · ${estado}`, 10, 20, { maxWidth: pw - 20 });

  const filas = gastos.map(g => [
    fechaReporte(g.date || g.created_at), g.numero || "",
    sinFactura(g) ? "Sin factura" : `Factura ${g.invoice_number || g.numero_comprobante || ""}`,
    g.provider || "—", g.description || "", g.obra_nombre || g.obra_referencia || g.project_name || "—",
    g.solicitante || "—",
    g.iva_tipo === "exenta" ? "Sin IVA" : `${g.iva_tipo === "5" ? "5" : "10"}%`,
    gs(g.amount), gs(g.iva_amount), gs(totalDe(g)),
  ]);
  const suma = (f) => gastos.reduce((s, g) => s + (Number(f(g)) || 0), 0);
  autoTable(doc, {
    startY: 25,
    head: [["Fecha", "N°", "Comprobante", "Proveedor", "Descripción", "Obra", "Solicitante", "IVA", "Neto", "IVA Gs.", "Total"]],
    body: filas,
    foot: [["TOTALES", "", "", "", `${gastos.length} gasto(s)`, "", "", "", gs(suma(g => g.amount)), gs(suma(g => g.iva_amount)), gs(suma(totalDe))]],
    styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
    headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: "bold" },
    footStyles: { fillColor: [220, 235, 245], textColor: [40, 40, 40], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 249, 255] },
    columnStyles: { 8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" } },
    margin: { left: 10, right: 10 },
  });

  const porTasa = (t) => gastos.filter(g => !sinFactura(g) && (g.iva_tipo || "10") === t);
  const resumen = [
    ["Facturas con IVA 10%", gs(porTasa("10").reduce((s, g) => s + totalDe(g), 0)), `IVA 10%: ${gs(porTasa("10").reduce((s, g) => s + (Number(g.iva_amount) || 0), 0))}`],
    ["Facturas con IVA 5%", gs(porTasa("5").reduce((s, g) => s + totalDe(g), 0)), `IVA 5%: ${gs(porTasa("5").reduce((s, g) => s + (Number(g.iva_amount) || 0), 0))}`],
    ["Facturas sin IVA", gs(porTasa("exenta").reduce((s, g) => s + totalDe(g), 0)), ""],
    ["Gastos sin factura", gs(gastos.filter(sinFactura).reduce((s, g) => s + totalDe(g), 0)), ""],
    ["Total de gastos del cierre", gs(cierre.total ?? suma(totalDe)), ""],
  ];
  if (Number(cierre.fondo) > 0) {
    resumen.push(["Fondo fijo asignado", gs(cierre.fondo), ""]);
    resumen.push(["Saldo de caja al cierre", gs(cierre.saldo_al_cierre), ""]);
  }
  resumen.push([Number(cierre.fondo) > 0 ? "Monto a reponer (depósito para volver al fondo)" : "Monto a reponer", gs(cierre.monto_reposicion), ""]);
  if (cierre.estado === "repuesto" && cierre.monto_repuesto !== undefined) resumen.push(["Monto repuesto", gs(cierre.monto_repuesto), ""]);
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 6,
    head: [["Resumen", "Monto", ""]],
    body: resumen,
    styles: { fontSize: 8, cellPadding: 1.5 },
    headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 45, halign: "right", fontStyle: "bold" }, 2: { cellWidth: 50 } },
    margin: { left: 10 },
    tableWidth: 185,
  });

  let y = doc.lastAutoTable.finalY + 6;
  if (cierre.observaciones) {
    doc.setFontSize(8);
    doc.text(`Observaciones: ${cierre.observaciones}`, 10, y, { maxWidth: pw - 20 });
    y += 8;
  }
  const ph = doc.internal.pageSize.getHeight();
  if (y > ph - 30) { doc.addPage(); y = 20; }
  y += 14;
  doc.setDrawColor(150, 150, 150);
  doc.setFontSize(8);
  ["Rendido por", "Revisado por", "Autorizado por"].forEach((t, i) => {
    const x = 20 + i * 90;
    doc.line(x, y, x + 65, y);
    doc.text(t, x + 32.5, y + 4, { align: "center" });
  });
  guardarPDF(doc, `Cierre_Caja_Chica_${cierre.numero || "sin_numero"}.pdf`);
}
